import { update } from "lodash";

/**
 * RequestBuilder из nast-api реализует then(onFulfilled) без onRejected: `await builder`
 * при ошибке сервера не завершается, а ошибка уходит в unhandled rejection.
 * Явный .then() превращает запрос в обычный Promise, который корректно отклоняется.
 * @param {Object} request - RequestBuilder
 * @return {Promise} ответ сервера
 */
const send = (request) => request.then((response) => response)

/**
 * Запрос без глобального обработчика ошибок из _config/config.js. Для картинок (blob) он показал бы
 * «Сервер недоступен» на каждую недоступную миниатюру. Колбэк с именем config подменяет глобальный.
 * @param {Object} request - RequestBuilder
 * @return {Object} тот же RequestBuilder
 */
const withoutErrorToast = (request) => request.callback((error) => {
  throw error
}, 'config', 'catch')

/**
 *
 */
export default class RestApi {
  auth = {
    login: (data) => $app.api.post([ 'auth/login' ]).data(data),
    loginPin: (userId, data) => $app.api.post([ 'auth/login/users*/pin', userId ]).data(data),
    register: (iin, phone, data) => $app.api.post([ 'auth/register**', iin, phone ]).data(data),
    recover: (iin, data) => $app.api.post([ 'auth/recover*', iin ]).data(data),
    info: () => $app.api.get([ 'auth/info' ]),
    checkData: (data) => $app.api.post([ 'auth/check-data' ]).data(data),
    sendCode: (iin, phone) => $app.api.post([ 'sms/send-code**', iin, phone ]),
    checkCode: (iin, data) => $app.api.post([ 'sms/check-code*', iin ]).data(data),
    deleteAcc: (id) => $app.api.patch([ 'auth/delete/users*', id ]),
    checkIin: (iin) => $app.api.post([ 'iin-check*', iin ]),
    sendGuidData: (guidData) => $app.api.post('auth/uuid-data').data(guidData),
    session: () => $app.api.get([ 'session' ])
  };

  promotions = {
    get: (id) => $app.api.get([ 'promotions*', id, ]),
    getUsers: () => $app.api.get([ 'promotions/users', ]),
    edit: (id, data) => $app.api.patch([ 'promotions*', id, ]).data(data),
    create: (data) => $app.api.post([ 'promotions', ]).data(data),
    give: (id, data) => $app.api.post([ 'promotions*/give', id, ]).data(data),
    delete: (id) => $app.api.delete([ 'promotions*', id, ]),
    assignToAll: (id) => $app.api.post([ 'promotions*/assign-to-all', id, ]),
    assignToCompany: (id, companyId) => $app.api.post([ 'promotions*/assign-to-company*', id, companyId, ]),
    assignToBranch: (id, data) => $app.api.post([ 'promotions*/assign-to-branch', id, ]).data(data),
    assignToUsers: (id, usersList) => $app.api.post([ 'promotions*/assign-to-users', id, ]).data(usersList),
    removeFromUser: (id, userId) => $app.api.post([ 'promotions*/users*', id, userId, ]),
  };

  settings = {
    get: (id) => $app.api.get([ 'settings*', id, ]),
    edit: (id, data) => $app.api.patch([ 'settings*', id, ]).data(data),
  };

  stats = {
    get: (query) => $app.api.get([ 'prosper/stats*', query, ]),
  };

  companies = {
    get: (id) => $app.api.get([ 'companies*', id ]),
    getStatistics: (id) => $app.api.get([ 'companies*/statistics', id ]),
    getSlim: (id) => $app.api.get([ 'companies/slim', id ]),
    getAllByBusinessType: (businessType) => $app.api.get([ 'companies/all*', businessType ]),
    getUserData: (id) => $app.api.get([ 'companies/user-data*', id ]),
    getStats: () => $app.api.get([ 'companies/stats' ]),
    refresh: () => $app.api.get([ 'companies/refresh' ]),
    edit: (id, data) => $app.api.patch([ 'companies*', id ]).data(data),
    create: (data) => $app.api.post([ 'companies' ]).data(data),
    getAuthProcessNames: () => $app.api.get([ 'companies/auth-process-names' ]),
    getLegalTypes: () => $app.api.get([ 'companies/all/legalType' ]),
    
    // Schedule
    getSchedule: (companyId) => $app.api.get([ `schedule/${companyId}` ]),
    changeSchedule: (data, companyId) => $app.api.patch([ `schedule/${companyId}` ]).data(data),
    getScheduleTypes: () => $app.api.get([ `schedule/types` ]),
    createSchedule: (data) => $app.api.post([ 'schedule' ]).data(data),

    // Настроики платежной системы для компании
    getCompanyShortList: () => $app.api.get([ `companies/short-info` ]),
    changePayoutProvider: (data) => $app.api.put([ `companies/switch/payout-provider` ]).data(data),
    changeNotificationType: (data) => $app.api.put([ `companies/switch/notification-type` ]).data(data),

  };

  crmRent = {
    setEnabled: (companyId, enabled) => $app.api.put([ `crm-rent*/${companyId}/enabled?enabled=${enabled}` ]),
  };

  statistics = {
    getAcw: () => $app.api.get([ 'document/acw/new/statistics' ]),
    getFlows: () => $app.api.get([ 'document/acw/new/flows' ]),
    getAvailableRange: (companyId, params) =>
      $app.api.get([ 'document/acw*/available-range', companyId ]).query(params || {}),
    createAcwRequest: (data) => $app.api.post([ 'document/acw/new' ]).data(data),
    createAcwByBusinessType: (data) => $app.api.post([ 'document/acw/new/by-business-type' ]).data(data),
  }

  integration = {
    postFile: (companyId, file) => $app.api.post([ '/prosper/integration**/post-excel', companyId, ])
      .data(file)
      .config({
        headers: {
          'Content-type': 'multipart/form-data',
        },
        timeout: 3 * 60 * 1000 // 3 min
      }),
    history: (companyId) => $app.api.get([ '/prosper/integration-files**/history', companyId, ]),
    getTemplate: () => $app.api.get([ '/prosper/integration-files/template', ]),
    getTemplateFileById: (id) => $app.api.get([ '/integration/dynamic/templates', id, ]),
    deleteTemplate: (id) => $app.api.delete([ '/integration/dynamic/templates', id, ]),
    getAllTemplates: (companyId) => $app.api.get([ '/integration/dynamic/templates/company', companyId ]),
    getAllColumns: () => $app.api.get([ '/integration/dynamic/templates/columns' ]),
    postTemplatesFile: (companyId, file) => $app.api.post([ '/integration/dynamic/templates/upload', companyId ]).data(file).config({
      headers: {
        'Content-type': 'multipart/form-data',
      },
    }),
    createYandex: (data) => $app.api.post([ 'integration-service/yandex/integrations' ]).data(data),
    postCreateTemplate: (companyId, data) => $app.api.post([ '/integration/dynamic/templates', companyId ]).data(data),
    getHistoryList: (companyId) => $app.api.get([ '/integration/dynamic/templates/upload/history', companyId ]),
    getTemplateStatistic: (id) => $app.api.get([ '/integration/dynamic/templates/upload/statistic', id ]),
    getCreatedUsersStatistic: (id) => $app.api.get([ '/integration/dynamic/templates/upload/statistic/loading', id ]),
    getStatusTemplatesStatistic: (id) => $app.api.get([ '/integration/dynamic/templates/upload/status', id ]),
    downloadTemplateFile: (id) => $app.api.get([ '/integration/dynamic/templates/upload/download', id ]),
    downloadFileReport: (id) => $app.api.get([ '/integration/dynamic/templates/upload/statistics/failed-excel', id ]).config({
      responseType: 'blob',
    }),
    getReport: (id) => $app.api.get([ '/integration/dynamic/templates/upload/statistics/processed', id ])
  };

  visits = {
    get: (id) => $app.api.get([ 'companies*/visits', id, ]),
  };
  commissions = {
    get: (id) => $app.api.get([ 'commissions', id, ]),
    getParentUnit: (companyId) => $app.api.get([ 'commissions*/all/parent-unit', companyId, ]),
    getByCompany: (companyId) => $app.api.get([ 'commissions*/all', companyId, ]),
    getCalculatorData: (companyId, sum) => $app.api.get([ 'commissions/calculate*', companyId, sum ]),
    getRoles: () => $app.api.get([ 'commissions/commission-roles' ]),
    create: (companyId, data) => $app.api.post([ 'commissions*', companyId, ]).data(data),
    remove: (id) => $app.api.delete([ 'commissions*', id, ]),
  };

  limits = {
    get: (id) => $app.api.get([ 'limits', id, ]),
    getByCompany: (companyId) => $app.api.get([ 'limits*/all', companyId, ]),
    create: (companyId, data) => $app.api.post([ 'limits*/create', companyId, ]).data(data),
    remove: (id) => $app.api.post([ 'limits/delete*', id, ]),
  };

  lang = {
    locales: (lang) => $app.api.get([ 'locales', ]),
    items: () => $app.api.get([ 'translations/content', ]),
  };

  users = {
    get: (id) => $app.api.get([ 'users*', id, ]),
    getCompany: (id) => $app.api.get([ 'users-companies*', id, ]),
    getByCompany: (companyId) => $app.api.get([ 'companies*/users', companyId, ]),
    searchByFilter: (params) => $app.coolApi.get([ 'users' ]).query(params),
    edit: (id, data) => $app.api.patch([ 'users*', id, ]).data(data),
    editPassword: (id, data) => $app.api.patch([ 'users*/password', id, ]).data(data),
    sendCode: (id) => $app.api.post([ 'users*/send-code', id, ]),
    checkCode: (id, data) => $app.api.post([ 'users*/check-code', id, ]).data(data),
    search: (data, request) => $app.api.post([ 'users*/search', request, ]).data(data),
    editPin: (id, data) => $app.api.patch([ 'users*/edit-pin', id, ]).data(data),
    editLimit: (id, data) => $app.api.patch([ 'users*/limit', id, ]).data(data),
    getFree: (companyId) => $app.api.get([ 'companies*/users/free', companyId, ]),
    recover: (userId) => $app.api.patch([ 'users*/recover', userId, ]),
    updatePhone: (date) => $app.api.patch([ 'users/phone', ]).data(date),
  };

  roles = {
    getRoles: () => $app.api.get([ 'roles/get-all' ]),
    createRoles: (data) => $app.api.post([ 'roles/grant' ]).data(data),
    getUserRoles: (userId) => $app.api.get([ `roles/get-all/${userId}` ]),
    deleteRoles: (data) => $app.api.post([ 'roles/delete' ]).data(data),
  };

  branches = {
    get: (companyId, id) => $app.api.get([ 'companies*/branches*', companyId, id, ]),
    getSlim: (companyId, id) => $app.api.get([ 'companies*/branches/slim', companyId, id, ]),
    getAllForSelect: (companyId) => $app.api.get([ 'companies*/branches-for-select', companyId, ]),
    getUsersList: (companyId, id) => $app.api.get([ 'companies*/branches*/users', companyId, id, ]),
    create: (companyId, data) => $app.api.post([ 'companies*/branches', companyId, ]).data(data),
    edit: (companyId, id, data) => $app.api.patch([ 'companies*/branches*', companyId, id, ]).data(data),
    addUsers: (companyId, id, data) => $app.api.post([ 'companies*/branches*/add', companyId, id, ]).data(data),
    removeUser: (companyId, id, userId) => $app.api.post([ 'companies*/branches*/users*/remove', companyId, id, userId, ]),
    delete: (companyId, id) => $app.api.delete([ 'companies*/branches*', companyId, id, ]),
  };

  tags = {
    get: () => $app.api.get([ 'tags', ]),
    getByCompany: (companyId) => $app.api.get([ `companies*/${companyId}/tags` ]),
    deleteFromCompany: (companyId, tagId) => $app.api.delete([ `companies*/${companyId}/tags/${tagId}`, ]),
    addToCompany: (companyId, tagIds) => $app.api.post([ `companies*/${companyId}/tags/`, ]).data({ tagIds }),
    create: (data) => $app.api.post([ 'tags', ]).data(data),
    delete: (id) => $app.api.delete([ 'tags*', id, ]),
  };

  departments = {
    getSlim: (companyId, id) => $app.api.get([ 'companies*/departments', companyId, id, ]),
  };

  notifications = {
    get: (id) => $app.coolApi.get([ 'admin/notifications', id, ]),
    getById: (notificationId) => $app.coolApi.get([ 'admin/notifications', notificationId ]),
    getCategories: () => $app.coolApi.get([ 'admin/notifications/categories' ]),
    getUsers: () => $app.api.get([ 'users-companies*' ]),
    getUsersWithoutCompany: () => $app.api.get([ 'notifications/users', ]),
    send: (data, companyId) => {
      const request = $app.coolApi.post([ 'admin/notifications', ]).data(data).config({
        headers: {
          'Content-type': 'multipart/form-data',
        },
      })

      if (companyId !== undefined && companyId !== null) {
        return request.query({ companyId })
      }

      return request
    },
    publish: (notificationId, data = {}) => $app.coolApi.post([ 'admin/notifications', notificationId, 'publish' ]).data(data),
    attachImages: (notificationId, data) => $app.coolApi.post([ 'admin/notifications', notificationId, 'images' ]).data(data).config({
      headers: {
        'Content-type': 'multipart/form-data',
      },
    }),
    notifyAllUsers: (data, companyId) => {
      const request = $app.coolApi.post([ 'admin/notifications', ]).data(data).config({
        headers: {
          'Content-type': 'multipart/form-data',
        },
      })

      if (companyId !== undefined && companyId !== null) {
        return request.query({ companyId })
      }

      return request
    },
  };

  accessBlocks = {
    getAll: () => $app.api.get([ 'accessBlock*', ]),
    edit: (id, data) => $app.api.patch([ 'accessBlock/updateEndTime*', id, ]).data(data),
    save: (data) => $app.api.post([ 'accessBlock', ]).data(data),
    stop: (id) => $app.api.patch([ 'accessBlock/stop*', id, ]),
  };

  mailing = {
    get: () => $app.api.get([ 'mailing/users/all', ]),
    add: (data) => $app.api.post([ 'mailing/users/add', ]).data(data),
    delete: (id) => $app.api.patch([ 'mailing/users/delete*', id, ]),
  };

  transactions = {
    get: (companyId) => $app.api.get([ 'transactions', companyId, ]),
    getWithUserAndCompanyInfo: () => $app.api.get([ 'transactions/with-user-and-company-info' ]),
    check: (companyId, userId, data) => $app.api.post([ 'companies*/users*/transactions/check', companyId, userId, ]).data(data),
    create: (companyId, userId, data) => $app.api.post([ 'companies*/users*/transactions', companyId, userId, ]).data(data),
    getListOld: (companyId, userId, request = '') => $app.api.get([ 'companies*/users*/transactions' + request, companyId, userId, ]),
    getList: () => $app.api.get([ '/transactions' ]),
    getAllByStatus: (request = '') => $app.api.get([ '/transactions' + request ]),
    updateStatusOld: (id, data) => $app.api.post([ '/transaction**/update-status', id, ]).data(data),
    updateStatus: (id, data) => $app.api.post([ '/transactions*/update-status', id, ]).data(data),
    accept: (data) => $app.api.post([ 'transaction/approve' ]).data(data),
    reject: (data) => $app.api.post([ 'transaction/reject' ]).data(data),
    enable: () => $app.api.patch([ 'payout-provider/enable' ]).data(),
    disable: () => $app.api.patch([ 'payout-provider/disable' ]).data(),
    getPayoutProviders: () => $app.api.get([ 'payout-provider', ]),
    getPayoutProviderBalances: () => $app.api.get([ 'payout-provider/balances', ]),
    restart: (query) => $app.api.patch([ 'transactions/restart' ]).query(query),
  };

  contacts = {
    get: () => $app.api.get([ 'contacts', ]),
  };

  application = {
    getByUser: (userId) => $app.api.get([ 'applications/users*', userId, ]),
    get: (id) => $app.api.get([ 'applications', id, ]),
    create: (data) => $app.api.post([ 'applications', ]).data(data),
    edit: (id, data) => $app.api.patch([ 'applications*', id, ]).data(data),
  };

  faqs = {
    get: (id) => $app.api.get([ 'faqs*', id, ]),
    create: (data) => $app.api.post([ 'faqs', ]).data(data),
    edit: (id, data) => $app.api.patch([ 'faqs*', id, ]).data(data),
  };

  logs = {
    get: () => $app.api.get([ 'logs', ]),
  };

  filials = {
    get: (id) => $app.api.get([ 'companies*/filials', id, ]),
    create: (id, data) => $app.api.post([ 'companies*/filials', id, ]).data(data),
    delete: (id) => $app.api.delete([ 'filials*', id, ]),
    getById: (id) => $app.api.get([ 'filials*', id, ]),
    getUsers: (id) => $app.api.get([ 'filials*/users', id ]),
    getFreeUsers: (id) => $app.api.get([ 'filials*/users/free', id ]),
    addUsers: (id, data) => $app.api.post([ 'filials*/users', id ]).data(data),
    removeUser: (id, userId) => $app.api.delete([ 'filials*/users*', id, userId ]),
    edit: (id, data) => $app.api.put([ 'filials*', id ]).data(data),
  };
  antifraud = {
    all: () => $app.api.get([ 'transaction-requests/all' ]),
    history: () => $app.api.get([ 'transaction-requests/history' ]),
    actual: () => $app.api.get([ 'transaction-requests/actual' ])
  };

  history = {
    getHistoryAction: (userId) => $app.api.get([ `user-actions/get/${userId}` ]),
  };

  holidays = {
    getAll: () => $app.api.get([ 'holidays' ]),
    create: (data) => $app.api.post([ 'holidays' ]).data(data),
    delete: (id) => $app.api.delete([ `holidays/${id}` ]),
  };
  utils = {
    downloadFile: (fullUrl, fileName) => {
      const match = fullUrl.match(/\/v[0-9]+\b/);
      if (!match) throw new Error('Некорректный url');
      const url = fullUrl.slice(match.index + match[0].length);

      $app.api.get( url ).config({
        responseType: 'blob',
      }).then((response) => {
        const blob = new Blob([ response.data ], { type: response.headers?.['content-type'] || 'application/octet-stream' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(link.href);
      }).catch((error) => {
        console.error('Ошибка при скачивании файла:', error);
      });
    },
  };
  balanceTransfer = {
    getTransfersList: (companyId) => $app.api.get([ 'balance/transfer/* ', companyId ]),
    putApproveTransfer: (transferId, data) =>
      $app.api.put([ 'balance/transfer*/approval', transferId ]).data(data),
    postTransfer: (data) => $app.api.post([ 'balance/transfer' ]).data(data),
  };

  prosperStart = {
    getRequests: () => $app.api.get([ 'onboarding/quota/cam/requests/get-all' ]),
    getPackages: () => $app.api.get([ 'onboarding/quota/cam/get-all' ]),
    rejectRequest: (id, data) => $app.api.post([ 'onboarding/quota/cam/reject', id ]).data(data),
    approveRequest: (id, data) => $app.api.post([ 'onboarding/quota/cam/approve', id ]).data(data),
  };

  snr = {
    getSubscriptionSettings: (query) => $app.api.get([ `subscription-settings${query}` ]),
    updateSubscriptionSettings: (id, data) => $app.api.patch([ `subscription-settings/${id}` ]).data(data),
    createSubscription: (data) => $app.api.post([ 'subscription-settings/create' ]).data(data),
    activateSubscription: (id, data) => $app.api.patch([ `subscription-settings/${id}/enable?enabled=${data}` ]),
    getLegalType: () => $app.api.get([ 'companies/by-legal-type?legalType=internet_platform_operator' ]),
    getSubscriptionStats: (queryParams) => $app.api.get([ 'user-subscriptions/stats' ]).query(queryParams ?? {}),
  };

  ipSnr = {
    getCompanies: (queryParams) => $app.api.get([ 'snr-report' ]).query(queryParams ?? {}),
    getCompanyById: (companyId, queryParams) => $app.api.get([ 'snr-report*', companyId ]).query(queryParams ?? {}),
    downloadExcel: () => $app.api.post([ 'snr-report/excel/download' ]).config({
      responseType: 'blob',
    }),
    downloadExcelByCompanyId: (companyId) => $app.api.post([ 'snr-report*/excel/download', companyId ]).config({
      responseType: 'blob',
    }),
    getFiscalizations: () => $app.api.get([ 'fiscalizations/user/history' ]),
    getFiscalizationStats: () => $app.api.get([ 'fiscalizations/user/total' ]),
    getCompanyStats: (companyId) => $app.api.get([ 'snr-report*/stats', companyId ]),
    getCompaniesStats: () => $app.api.get([ 'snr-report/stats' ]),
  };

  fiscalizations = {
    getFiscalizations: (queryParams) => $app.api.get([ 'fiscalizations/rollback/tasks' ]).query(queryParams || {}),
    createTask: (data) => $app.api.post([ 'fiscalizations/rollback/tasks/create' ]).data(data),
    getSettings: (companyId) => $app.api.get([ 'company*/fiscalizations/settings', companyId ]),
    updateSettings: (companyId, data) => $app.api.patch([ 'company*/fiscalizations/settings', companyId ]).data(data),
  }

  reconciliation = {
    getList: (queryParams) => $app.api.get([ 'reconciliations' ]).query(queryParams ?? {}),
    getTask: (taskId) => $app.api.get([ 'reconciliations*', taskId ]),
    create: (formData) => $app.api.post([ 'reconciliations' ])
    .data(formData)
    .config({
      headers: {
        'Content-type': 'multipart/form-data',
      }
    })
    ,
    downloadResult: (taskId) => $app.api
    .get([ 'reconciliations*/records/export', taskId ])
      .config({ responseType: 'blob' }),
  }

  refund = {
    create: (reconciliationTaskId) => $app.api.post([ 'refunds/reconciliations*', reconciliationTaskId ]),
    getTask: (refundId) => $app.api.get([ 'refunds*', refundId ]),
    downloadFile: (refundId) => $app.api.get([ 'refunds*/file', refundId ]),
    uploadStatement: (formData) => $app.api.post([ 'refunds/statements' ])
    .data(formData)
    .config({
      headers: {
        'Content-type': 'multipart/form-data',
      }
    }),
    getStatements: (queryParams) => $app.api.get([ 'refunds/statements' ]).query(queryParams ?? {}),
  }

  kgdRegistry = {
    get: (params) => $app.api.get([ 'taxes-registry' ]).query(params),
    export: (params) => $app.api.get([ 'taxes-registry/export' ]).query(params).config({
      responseType: 'blob',
    }),
  };

  reconciliationOfWithheldTaxes = {
    uploadFile: (file) => $app.api.post([ 'snr-calculator/calculate/excel' ])
      .data(file)
      .config({
        headers: {
          'Accept-Language': 'en-US',
        }
      }),
    generateFile: (file) => $app.api.post([ 'snr-calculator/calculate/excel/generate' ])
      .data(file)
      .config({
        responseType: 'blob',
        headers: {
          'Accept-Language': 'en-US',
        }
      }),
  }

  companyBalance = {
    topUp: (formData) => $app.api.post([ 'company-balance/top-ups' ])
      .data(formData),
  }
  /**
   * Prosper Connect (PP-901): те же ручки, что у партнёрского кабинета.
   * Контент — content-service (/api/v1/content/...), пуши — админский двойник
   * notification-service (/api/v2/admin/notifications), аудитории пушей — монолит.
   * Все методы возвращают Promise (см. send выше), а не RequestBuilder.
   */
  prosperConnect = {
    // Справочник компаний для сотрудника (не /by-user — под сотрудником он не работает)
    getCompanies: () => send($app.api.get([ 'content/companies' ])),

    // Списки: статьи, сторисы, FAQ
    getList: (type, params) => send($app.api.get([ `content/${type}/list` ]).query(params || {})),
    setStatus: (type, id, status) => send($app.api.patch([ `content/${type}/${id}/status?status=${status}` ])),
    remove: (type, id) => send($app.api.delete([ `content/${type}/${id}` ])),

    // Статьи
    getArticle: (id) => send($app.api.get([ `content/articles/${id}` ])),
    createArticle: (data) => send($app.api.post([ 'content/articles' ]).data(data)),
    updateArticle: (id, data) => send($app.api.put([ `content/articles/${id}` ]).data(data)),
    bulkArchiveArticles: (articleIds) => send($app.api.post([ 'content/articles/bulk/archive' ]).data({ articleIds })),
    bulkDeleteArticles: (articleIds) => send($app.api.post([ 'content/articles/bulk/delete' ]).data({ articleIds })),
    uploadArticleImage: (formData) => send($app.api.post([ 'content/article-content/upload/image' ])
      .data(formData)
      .config({ headers: { 'Content-type': 'multipart/form-data' } })),
    getArticleImage: (contentName) => send(withoutErrorToast($app.api.get([ 'content/article-content' ])
      .query({ contentName: encodeURIComponent(contentName) })
      .config({ responseType: 'blob' }))),

    // Сторисы
    getStory: (id) => send($app.api.get([ `content/story-block/${id}` ])),
    createStory: (data) => send($app.api.post([ 'content/story-block' ]).data(data)),
    updateStory: (id, data) => send($app.api.put([ `content/story-block/${id}` ]).data(data)),
    // У сторис массовые действия другие, чем у статей: статус — PATCH, удаление — DELETE с телом
    bulkStoryStatus: (storyBlockIds, status) => send($app.api.patch([ `content/story-block/bulk/status?status=${status}` ])
      .data({ storyBlockIds })),
    bulkDeleteStories: (storyBlockIds) => send($app.api.delete([ 'content/story-block/bulk/delete' ])
      .data({ storyBlockIds })),
    uploadStoryImage: (formData) => send($app.api.post([ 'content/story-content/upload/image' ])
      .data(formData)
      .config({ headers: { 'Content-type': 'multipart/form-data' } })),
    uploadStoryVideo: (formData) => send($app.api.post([ 'content/story-content/upload/video' ])
      .data(formData)
      .config({ headers: { 'Content-type': 'multipart/form-data' } })),
    getStoryMedia: (contentName) => send(withoutErrorToast($app.api.get([ 'content/story-content/block' ])
      .query({ contentName: encodeURIComponent(contentName) })
      .config({ responseType: 'blob' }))),

    // FAQ: сотрудник создаёт через by-category (by-company — партнёрская логика)
    createFaq: (data) => send($app.api.post([ 'content/faq/by-category' ]).data(data)),
    updateFaq: (id, data) => send($app.api.put([ `content/faq/${id}` ]).data(data)),
    bulkHideFaq: (faqIds) => send($app.api.post([ 'content/faq/bulk/hide' ]).data({ faqIds })),
    bulkDeleteFaq: (faqIds) => send($app.api.post([ 'content/faq/bulk/delete' ]).data({ faqIds })),
    getFaqCategories: () => send($app.api.get([ 'content/faq-category' ])),
    createFaqCategory: (data) => send($app.api.post([ 'content/faq-category' ]).data(data)),
    updateFaqCategory: (id, data) => send($app.api.put([ `content/faq-category/${id}` ]).data(data)),
    deleteFaqCategory: (id) => send($app.api.delete([ `content/faq-category/${id}` ])),

    // Push-уведомления: админский путь /api/v2/admin/notifications
    getNotifications: (params) => send($app.coolApi.get([ 'admin/notifications' ]).query(params || {})),
    getNotification: (id) => send($app.coolApi.get([ `admin/notifications/${id}` ])),
    // companyId обязателен, иначе 400; GLOBAL-рассылки в счётчики не попадают
    getNotificationStatusCounts: (companyId) => send($app.coolApi.get([ 'admin/notifications/status-counts' ])
      .query({ companyId })),
    getNotificationCategories: () => send($app.coolApi.get([ 'admin/notifications/categories' ])),
    createNotification: (formData, companyId) => {
      const request = $app.coolApi.post([ 'admin/notifications' ]).data(formData).config({
        headers: { 'Content-type': 'multipart/form-data' },
      })
      return send(companyId ? request.query({ companyId }) : request)
    },
    patchNotification: (id, data) => send($app.coolApi.patch([ `admin/notifications/${id}` ]).data(data)),
    // Тело обязательно, хотя бы пустое {}
    publishNotification: (id, data) => send($app.coolApi.post([ `admin/notifications/${id}/publish` ]).data(data || {})),
    cancelNotification: (id) => send($app.coolApi.post([ `admin/notifications/${id}/cancel` ])),
    repeatNotification: (id) => send($app.coolApi.post([ `admin/notifications/${id}/repeat` ])),
    deleteNotification: (id) => send($app.coolApi.delete([ `admin/notifications/${id}` ])),
    attachNotificationImages: (id, formData) => send($app.coolApi.post([ `admin/notifications/${id}/images` ])
      .data(formData)
      .config({ headers: { 'Content-type': 'multipart/form-data' } })),
    deleteNotificationImage: (id, lang) => send($app.coolApi.delete([ `admin/notifications/${id}/images/${lang}` ])),
    importNotificationRecipients: (id, formData) => send($app.coolApi.post([ `admin/notifications/${id}/recipients/import` ])
      .data(formData)
      .config({ headers: { 'Content-type': 'multipart/form-data' } })),

    // Аудитории пушей — монолит, как в кабинете
    getCompanyDepartments: (companyId) => send($app.api.get([ `companies/${companyId}/departments` ])),
    // Ответ постраничный (по умолчанию 100) — берём максимум, который разрешает Spring (2000)
    getCompanyUsers: (companyId) => send($app.api.get([ `companies/${companyId}/users` ])
      .query({ 'filter[status]': 'not:fired', all: true, size: 2000 })),
  };

  // Овердрафт партнёров (Prosperpay-1151): balance-service, /api/v2/admin/overdraft, только ФД.
  // Ошибки показывает сам раздел — у полей формы, поэтому глобальные тосты отключены.
  overdraft = {
    getPartners: (params) => send(withoutErrorToast($app.coolApi.get([ 'admin/overdraft/partners' ])
      .query(params || {}))),
    addPartner: (data) => send(withoutErrorToast($app.coolApi.post([ 'admin/overdraft/partners' ]).data(data))),
    getPartner: (companyId) => send(withoutErrorToast($app.coolApi.get([ `admin/overdraft/partners/${companyId}` ]))),
    savePartner: (companyId, data) => send(withoutErrorToast($app.coolApi
      .put([ `admin/overdraft/partners/${companyId}` ]).data(data))),
    clearLimits: (companyId, expectedVersion, idempotencyKey) => send(withoutErrorToast($app.coolApi
      .delete([ `admin/overdraft/partners/${companyId}/limits` ]).query({ expectedVersion, idempotencyKey, }))),
    getChangeLog: (companyId, params) => send(withoutErrorToast($app.coolApi
      .get([ `admin/overdraft/partners/${companyId}/limit-log` ]).query(params || {}))),
    getRequests: (companyId, params) => send(withoutErrorToast($app.coolApi
      .get([ `admin/overdraft/partners/${companyId}/requests` ]).query(params || {}))),
    getLines: (companyId, params) => send(withoutErrorToast($app.coolApi
      .get([ `admin/overdraft/partners/${companyId}/lines` ]).query(params || {}))),
    searchCompanies: (search) => send(withoutErrorToast($app.coolApi.get([ 'admin/overdraft/companies' ])
      .query({ search, }))),
  };
}
